import {
  FontAwesomeIcon,
  FontAwesomeIconProps,
} from "@fortawesome/react-fontawesome";
import { cva, type VariantProps } from "class-variance-authority";
import React, { forwardRef, ImgHTMLAttributes } from "react";
import { cn } from "../lib/utils";
import type { IconName } from "./icon-components";
import { availableIcons, icons } from "./icon-components";

// Define a type that handles both string paths and Next.js StaticImageData
type IconAsset =
  | string
  | { src: string; height?: number; width?: number; blurDataURL?: string };

// Define the icon variants using class-variance-authority
const iconVariants = cva("", {
  variants: {
    size: {
      sm: "w-4 h-4",
      md: "w-6 h-6",
      lg: "w-8 h-8",
    },
  },
  defaultVariants: {
    size: "md",
  },
});

// Define the props for the Icon component
export type IconProps = VariantProps<typeof iconVariants> & {
  name: IconName;
  className?: string;
} & Omit<React.HTMLAttributes<HTMLElement>, "color">;

// Re-export the IconName type for convenience
export type { IconName };

// Re-export isIconName and availableIcons
export const isIconName = (name: unknown): name is IconName =>
  typeof name === "string" && icons.isValid(name);

export { availableIcons };

// Helper function to normalize the icon asset to get the src regardless of format
const getIconSrc = (asset: IconAsset): string => {
  if (typeof asset === "string") return asset;
  return asset.src;
};

// The Icon component implementation
export const Icon = forwardRef<HTMLElement, IconProps>(
  ({ name, size, className, ...props }, ref) => {
    if (icons.isFa(name)) {
      const icon = icons.getFa(name);

      // Extract only the props that FontAwesomeIcon accepts
      const faProps: FontAwesomeIconProps = {
        icon,
        className: cn(iconVariants({ size }), className),
      };

      return (
        <FontAwesomeIcon
          {...faProps}
          // FontAwesomeIcon expects SVGSVGElement ref
          ref={ref as React.Ref<SVGSVGElement>}
        />
      );
    }

    if (icons.isSvg(name)) {
      const icon = icons.getSvg(name);
      return (
        <img
          src={getIconSrc(icon)}
          alt={`${name} icon`}
          className={cn(iconVariants({ size }), className)}
          {...(props as ImgHTMLAttributes<HTMLImageElement>)}
          ref={ref as React.Ref<HTMLImageElement>}
        />
      );
    }

    if (icons.isReact(name)) {
      const ReactIcon = icons.getReact(name);

      const reactProps = {
        className: cn(iconVariants({ size }), className),
        ...props,
      };
      return <ReactIcon {...reactProps} />;
    }

    if (icons.isPng(name)) {
      const icon = icons.getPng(name);
      return (
        <img
          src={getIconSrc(icon)}
          alt={`${name} icon`}
          className={cn(iconVariants({ size }), className)}
          {...(props as ImgHTMLAttributes<HTMLImageElement>)}
          ref={ref as React.Ref<HTMLImageElement>}
        />
      );
    }

    console.warn(`Invalid icon name: ${name}`);
    return null;
  },
);

Icon.displayName = "Icon";
